(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  let collectionRequest;

  const updateCollectionResults = async (sortValue, updateHistory = true) => {
    const results = document.querySelector('[data-collection-results]');
    if (!results) return;

    collectionRequest?.abort();
    const request = new AbortController();
    collectionRequest = request;
    const url = new URL(location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.delete('page');
    results.classList.add('is-loading');
    results.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        signal: request.signal,
      });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextResults = nextDocument.querySelector('[data-collection-results]');
      if (!nextResults) throw new Error('Collection results were not found');

      results.innerHTML = nextResults.innerHTML;
      if (updateHistory) history.replaceState({ westerSort: sortValue }, '', url);
      if (nextDocument.title) document.title = nextDocument.title;
      requestAnimationFrame(() => results.classList.remove('is-loading'));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Wester collection sort]', error);
        results.classList.remove('is-loading');
      }
    } finally {
      if (collectionRequest === request) {
        results.removeAttribute('aria-busy');
        collectionRequest = undefined;
      }
    }
  };

  document.querySelectorAll('[data-sort-rail]').forEach((rail) => {
    const track = rail.querySelector('[data-sort-track]');
    const options = [...rail.querySelectorAll('[data-sort-option]')];
    const select = rail.querySelector('[data-collection-sort]');
    if (!track || !options.length || !select) return;

    const selected = options.findIndex((option) => option.classList.contains('is-selected'));
    let committedIndex = selected < 0 ? 0 : selected;
    let previewIndex = committedIndex;
    let dragging = false;

    const paint = (index) => {
      previewIndex = clamp(index, 0, options.length - 1);
      rail.style.setProperty('--sort-index', String(previewIndex));
      rail.style.setProperty('--sort-position', `${((previewIndex + .5) / options.length) * 100}%`);
      options.forEach((option, optionIndex) => {
        option.classList.toggle('is-selected', optionIndex === previewIndex);
      });
    };

    const indexFromPointer = (clientX) => {
      const rect = track.getBoundingClientRect();
      const step = rect.width / options.length;
      return Math.round(clamp((clientX - rect.left - step / 2) / step, 0, options.length - 1));
    };

    const commit = (index) => {
      const nextIndex = clamp(index, 0, options.length - 1);
      paint(nextIndex);
      if (nextIndex === committedIndex) return;
      committedIndex = nextIndex;
      options.forEach((option, optionIndex) => {
        option.setAttribute('aria-pressed', String(optionIndex === committedIndex));
      });
      select.value = options[committedIndex].dataset.sortValue || '';
      updateCollectionResults(select.value);
    };

    options.forEach((option, index) => {
      option.addEventListener('click', () => commit(index));
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      dragging = true;
      rail.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
      paint(indexFromPointer(event.clientX));
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      paint(indexFromPointer(event.clientX));
    });

    track.addEventListener('pointerup', (event) => {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      commit(previewIndex);
    });

    track.addEventListener('pointercancel', (event) => {
      dragging = false;
      rail.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      paint(committedIndex);
    });

    paint(committedIndex);
  });

  document.querySelectorAll('[data-collection-handwriting]').forEach((text) => {
    if (reducedMotion) return;
    requestAnimationFrame(() => text.classList.add('is-writing'));
  });

  document.querySelectorAll('.wr-collection-page').forEach((page) => {
    const vaporItems = [...page.querySelectorAll('[data-collection-vapor]')];
    if (!vaporItems.length || reducedMotion) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      const distance = Math.max(260, Math.min(innerHeight * .72, 620));
      const progress = clamp(scrollY / distance, 0, 1);
      vaporItems.forEach((item, index) => {
        const localProgress = clamp((progress * 1.35) - (index * .12), 0, 1);
        item.style.setProperty('--collection-vapor-progress', localProgress.toFixed(4));
      });
    };

    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    addEventListener('scroll', requestUpdate, { passive: true });
    addEventListener('resize', requestUpdate, { passive: true });
  });
})();
